import { LOCALE_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { UtilService } from 'common/services/utilService'

export type ScheduleColumnWidthView = 'day' | 'week'

type UserColumnWidths = Partial<Record<ScheduleColumnWidthView, Record<string, number>>>

type ScheduleColumnWidthsStore = Record<string, UserColumnWidths>

const readStore = (): ScheduleColumnWidthsStore => {
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEYS.SCHEDULE_COLUMN_WIDTHS)
    if (!raw) return {}
    const parsed = UtilService.safeParseJSON<ScheduleColumnWidthsStore | null>(raw, null)
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed
  } catch {
    return {}
  }
}

const writeStore = (store: ScheduleColumnWidthsStore): void => {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEYS.SCHEDULE_COLUMN_WIDTHS, JSON.stringify(store))
  } catch {
    // Storage can fail (private mode, quota); ignore.
  }
}

export const getScheduleColumnWidthOverrides = (
  userId: number | null | undefined,
  view: ScheduleColumnWidthView,
): Record<string, number> => {
  if (userId == null || Number.isNaN(Number(userId))) return {}
  const userEntry = readStore()[String(userId)]
  const overrides = userEntry?.[view]
  if (!overrides || typeof overrides !== 'object') return {}

  const cleaned: Record<string, number> = {}
  for (const [key, value] of Object.entries(overrides)) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) continue
    if (view === 'week' && /^\d{4}-\d{2}-\d{2}$/.test(key)) continue
    cleaned[key] = value
  }
  return cleaned
}

export const setScheduleColumnWidthOverride = (
  userId: number | null | undefined,
  view: ScheduleColumnWidthView,
  columnKey: string,
  referenceWidthPx: number,
): Record<string, number> => {
  if (userId == null || Number.isNaN(Number(userId)) || !columnKey) {
    return getScheduleColumnWidthOverrides(userId, view)
  }

  const store = readStore()
  const userKey = String(userId)
  const userEntry = store[userKey] ?? {}
  const nextView = {
    ...(userEntry[view] ?? {}),
    [columnKey]: referenceWidthPx,
  }
  store[userKey] = { ...userEntry, [view]: nextView }
  writeStore(store)
  return nextView
}
